import React, { FormEvent, useEffect, useState } from "react";
import api from "../../../api";
import "../../../styles/Login.scss";
import {IMAGE_URL} from "../../../path";

interface Page {
    id: number;
    name: string;
    slug: string;
}

interface PageHeader {
    id: number;
    page_id: number;
    image: string;
    mobile_image: string | null;
    link: string | null;
    active: boolean;
    display_order: number;
}

interface PageHeaderModalProps {
    header?: PageHeader | null;
    pages: Page[];
    onClose: () => void;
    onSaved: () => void;
}

const PageHeaderModal = ({
                             header,
                             pages,
                             onClose,
                             onSaved,
                         }: PageHeaderModalProps) => {
    const isEditing = !!header;

    const [pageId, setPageId] = useState<string>("");
    const [image, setImage] = useState<File | null>(null);
    const [mobileImage, setMobileImage] = useState<File | null>(null);
    const [link, setLink] = useState<string>("");
    const [active, setActive] = useState<boolean>(true);
    const [displayOrder, setDisplayOrder] = useState<string>("0");

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (header) {
            setPageId(String(header.page_id));
            setLink(header.link || "");
            setActive(header.active);
            setDisplayOrder(String(header.display_order));
            setImage(null);
            setMobileImage(null);
        } else {
            setPageId(
                pages.length > 0
                    ? String(pages[0].id)
                    : ""
            );
            setLink("");
            setActive(true);
            setDisplayOrder("0");
            setImage(null);
            setMobileImage(null);
        }

        setError("");
    }, [header, pages]);

    const handleSubmit = async (
        event: FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        setLoading(true);
        setError("");

        try {
            if (!pageId) {
                setError("Please select a page.");
                setLoading(false);
                return;
            }

            if (!isEditing && !image) {
                setError("Please select a desktop image.");
                setLoading(false);
                return;
            }

            const formData = new FormData();

            formData.append("page_id", pageId);
            formData.append("link", link);
            formData.append(
                "active",
                active ? "1" : "0"
            );
            formData.append(
                "display_order",
                displayOrder
            );

            if (image) {
                formData.append("image", image);
            }

            if (mobileImage) {
                formData.append(
                    "mobile_image",
                    mobileImage
                );
            }

            if (isEditing) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/page-headers/${header.id}`,
                    formData
                );
            } else {
                await api.post(
                    "/admin/page-headers",
                    formData
                );
            }

            onSaved();
            onClose();
        } catch (err: any) {
            console.error(
                "Failed to save page header:",
                err
            );

            const validationErrors =
                err.response?.data?.errors;

            if (validationErrors) {
                const firstError = Object.values(
                    validationErrors
                )
                    .flat()
                    .find(
                        (
                            error
                        ): error is string =>
                            typeof error === "string"
                    );

                setError(
                    firstError ||
                    "Unable to save page header. Please try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Unable to save page header. Please try again."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal"
                onClick={(event) =>
                    event.stopPropagation()
                }
            >
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit page header"
                            : "Add page header"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="admin-modal-content">

                        {/* PAGE */}

                        <div className="form-group">
                            <label htmlFor="page-header-page">
                                Page
                            </label>

                            <select
                                id="page-header-page"
                                value={pageId}
                                onChange={(event) =>
                                    setPageId(
                                        event.target.value
                                    )
                                }
                                disabled={loading}
                                required
                            >
                                <option value="">
                                    Select a page
                                </option>

                                {pages.map((page) => (
                                    <option
                                        key={page.id}
                                        value={page.id}
                                    >
                                        {page.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* DESKTOP IMAGE */}

                        <div className="form-group">
                            <label htmlFor="page-header-image">
                                Desktop image
                            </label>

                            {isEditing &&
                                header?.image && (
                                    <div
                                        style={{
                                            marginBottom:
                                                "12px",
                                        }}
                                    >
                                        <img
                                            src={`${IMAGE_URL}/${header.image}`}
                                            alt=""
                                            style={{
                                                width: "100%",
                                                maxHeight:
                                                    "180px",
                                                objectFit:
                                                    "cover",
                                                borderRadius:
                                                    "8px",
                                            }}
                                        />
                                    </div>
                                )}

                            <input
                                id="page-header-image"
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    setImage(
                                        event.target
                                            .files?.[0] ||
                                        null
                                    );
                                }}
                                required={!isEditing}
                                disabled={loading}
                            />

                            {isEditing && (
                                <small>
                                    Leave blank to keep the
                                    current desktop image.
                                </small>
                            )}
                        </div>

                        {/* MOBILE IMAGE */}

                        <div className="form-group">
                            <label htmlFor="page-header-mobile-image">
                                Mobile image
                            </label>

                            {isEditing &&
                                header?.mobile_image && (
                                    <div
                                        style={{
                                            marginBottom:
                                                "12px",
                                        }}
                                    >
                                        <img
                                            src={
                                                `${IMAGE_URL}/${header.mobile_image}`
                                            }
                                            alt=""
                                            style={{
                                                width: "100%",
                                                maxHeight:
                                                    "180px",
                                                objectFit:
                                                    "cover",
                                                borderRadius:
                                                    "8px",
                                            }}
                                        />
                                    </div>
                                )}

                            <input
                                id="page-header-mobile-image"
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                    setMobileImage(
                                        event.target
                                            .files?.[0] ||
                                        null
                                    );
                                }}
                                required={!isEditing}
                                disabled={loading}
                            />

                            {isEditing && (
                                <small>
                                    Leave blank to keep the
                                    current mobile image.
                                </small>
                            )}
                        </div>

                        {/* LINK */}

                        <div className="form-group">
                            <label htmlFor="page-header-link">
                                Link
                            </label>

                            <input
                                id="page-header-link"
                                type="text"
                                value={link}
                                onChange={(event) =>
                                    setLink(
                                        event.target.value
                                    )
                                }
                                placeholder="Optional"
                                disabled={loading}
                            />
                        </div>

                        {/* DISPLAY ORDER */}

                        <div className="form-group">
                            <label htmlFor="page-header-order">
                                Display order
                            </label>

                            <input
                                id="page-header-order"
                                type="number"
                                min="0"
                                value={displayOrder}
                                onChange={(event) =>
                                    setDisplayOrder(
                                        event.target.value
                                    )
                                }
                                required
                                disabled={loading}
                            />
                        </div>

                        {/* ACTIVE */}

                        <div className="form-group">
                            <label
                                htmlFor="page-header-active"
                                style={{
                                    display: "flex",
                                    alignItems:
                                        "center",
                                    gap: "8px",
                                    marginBottom: 0,
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    id="page-header-active"
                                    type="checkbox"
                                    checked={active}
                                    onChange={(event) =>
                                        setActive(
                                            event.target
                                                .checked
                                        )
                                    }
                                    disabled={loading}
                                />

                                <span>Active</span>
                            </label>
                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="admin-modal-actions">
                        <button
                            type="button"
                            className="admin-table-button"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="admin-primary-button"
                            disabled={loading}
                        >
                            {loading
                                ? "Saving..."
                                : isEditing
                                    ? "Save changes"
                                    : "Add header"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PageHeaderModal;
